import { Hourglass, Upload, CheckCircle2, Mail, type LucideIcon } from "lucide-react";
import { cn } from "@/utils/utils";
import { homepageColumnTint } from "@/utils/homepageColours";
import { SubmissionRow } from "@/ui/molecules";
import type { THomepageColumn as THomepageColumnData, THomepageRow, THomepageView } from "@/store";

const COLUMN_ICONS: Record<THomepageColumnData["key"], LucideIcon> = {
    not_yet_due: Hourglass,
    upload_now: Upload,
    submitted: CheckCircle2,
    waiting_on_service_provider: Hourglass,
    ready_for_you: Mail,
    submitted_to_erp: CheckCircle2,
};

export interface THomepageColumnProps {
    column: THomepageColumnData;
    view: THomepageView;
    onRowClick?: (row: THomepageRow, columnKey: THomepageColumnData["key"]) => void;
    /** Reveal the rows this column is capped at. Omit to leave the footer as a
     * plain count — the cap is then simply reported, not escapable. */
    onShowMore?: () => void;
    /** A larger page is already in flight, so the button reads as busy and
     * cannot be clicked into a queue of overlapping fetches. */
    isLoadingMore?: boolean;
    className?: string;
}

/**
 * One column of the Homepage v2 board — icon + label + count header, tinted
 * body, scrollable row list (design doc §5.5, §7.1/§7.3).
 */
export function HomepageColumn({
    column,
    view,
    onRowClick,
    onShowMore,
    isLoadingMore = false,
    className,
}: THomepageColumnProps) {
    const Icon = COLUMN_ICONS[column.key];
    const tint = homepageColumnTint(column.key);

    return (
        <div
            data-testid={`homepage-column-${column.key}`}
            // The column itself stays white; the colour lives in the header
            // strip and in each card (§5.5).
            className={cn("flex flex-col rounded-xl border bg-card", className)}
        >
            <div
                className={cn(
                    "flex items-center justify-between rounded-t-xl px-3 py-2.5",
                    tint.header,
                )}
            >
                <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-foreground">
                    <Icon className="h-4 w-4" />
                    <span>{column.label}</span>
                </div>
                <span
                    data-testid="homepage-column-count"
                    className="text-sm font-semibold text-foreground"
                >
                    {column.count}
                </span>
            </div>
            {/* Fixed max-height with its own scroll, rather than a flex-grown
                height chased up through PageLayout/MainLayout — the page as a
                whole already scrolls (matches every other view), this just
                keeps one very long column from pushing the other two down. */}
            <div className="scrollbar-hover max-h-[65vh] space-y-2 overflow-y-auto p-2">
                {column.rows.map((row) => (
                    <SubmissionRow
                        key={`${row.dealId}-${row.period}-${row.fileType}`}
                        row={row}
                        view={view}
                        columnKey={column.key}
                        onClick={onRowClick ? () => onRowClick(row, column.key) : undefined}
                    />
                ))}
                {column.totalCount > column.rows.length && (
                    <div className="px-1 py-1 text-center text-[13px] text-muted-foreground">
                        <span>
                            Showing {column.rows.length} of {column.totalCount}
                        </span>
                        {onShowMore && (
                            <button
                                type="button"
                                onClick={onShowMore}
                                disabled={isLoadingMore}
                                data-testid="homepage-column-show-more"
                                className="ml-2 font-medium text-foreground underline underline-offset-2 hover:no-underline disabled:cursor-default disabled:opacity-60"
                            >
                                {isLoadingMore ? "Loading…" : "Show more"}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
